import useAppContext from '@groupefungo/react-apollo.core/app/UseContext';
import useAppointmentGql from '../../../wedding-wardrobe/src/components/appointments/hooks/useAppointmentGql';
import useAvailabilityGql from '../../../wedding-wardrobe/src/components/availabilities/hooks/useAvailabilityGql';

const initialCalendarGql = {
  appointmentTypes: [],
  calendarType: 'Week',
  withAppointments: false,
  withAvailabilities: false,
  withAvailability: false,
  withHourlyAppointments: false,
};

export default (queryParams = {}) => {
  const calendarQueriesParams = { ...initialCalendarGql, ...queryParams };
  const { gql, useQuery } = useAppContext();
  const { withAppointments, withAvailabilities, withAvailability, withHourlyAppointments, calendarType, appointmentType } = calendarQueriesParams;

  const { AppointmentFragment } = useAppointmentGql();
  const { AvailabilityScopedUsersAvailabilitiesFragment } = useAvailabilityGql();

  let calendarTypeQuery;
  switch (calendarType) {
    case 'Day':
      calendarTypeQuery = 'day';
      break;
    case 'Week':
      calendarTypeQuery = 'daysOfWeek';
      break;
    case 'Month':
      calendarTypeQuery = 'daysOfMonth';
      break;
    default:
      calendarTypeQuery = 'day';
      break;
  }

  const SchedulableAvailabilityFragment = gql`
fragment SchedulableAvailabilityFragment on SchedulableInterface {
  __typename
  availability {
    ...AvailabilityFragment
  }
}
${AvailabilityScopedUsersAvailabilitiesFragment({ forBooking: !!appointmentType })}
  `;

  const SchedulableAvailabilitiesFragment = gql`
fragment SchedulableAvailabilitiesFragment on SchedulableInterface {
  __typename
  availabilities {
    ...AvailabilityFragment
  }
}
${AvailabilityScopedUsersAvailabilitiesFragment({ forBooking: !!appointmentType })}
  `;
  const SchedulableAppointmentFragment = gql`
fragment SchedulableAppointmentFragment on SchedulableInterface {
  __typename
  appointments {
    ...AppointmentFragment
  }
}  
${AppointmentFragment}
  `;

  const WardrobeHourFragment = gql`
fragment WardrobeHourFragment on WardrobeHour {
  datetime
  utcDatetime
  formattedDatetime
  time
  disabled
}  
  `;
  const WardrobeDayAvailabilitiesFragment = gql`
fragment WardrobeDayAvailabilitiesFragment on WardrobeDay {
  availabilities {
    ...AvailabilityFragment
  }
}
${AvailabilityScopedUsersAvailabilitiesFragment({ forBooking: !!appointmentType })}  
  `;

  const WardrobeDayFragment = gql`
fragment WardrobeDayFragment on WardrobeDay {
  date
  dateString
  dayOfWeek
  disabled
  isWeekend
  month
  monthName
  utcDatetime
  weekDay
  weekDayName
  hours {
    ...WardrobeHourFragment
    ${withHourlyAppointments ? '...SchedulableAppointmentFragment' : ''}
    ${withAvailabilities ? '...SchedulableAvailabilitiesFragment' : ''}
    ${withAvailability ? '...SchedulableAvailabilityFragment' : ''}
  }
}
${WardrobeHourFragment}
${withHourlyAppointments ? SchedulableAppointmentFragment : ''}
${withAvailabilities ? SchedulableAvailabilitiesFragment : ''}
${withAvailability ? SchedulableAvailabilityFragment : ''}

  `;

  const WardrobeCalendarFragment = gql`
fragment WardrobeCalendarFragment on WardrobeCalendar {
  ${calendarTypeQuery} {
    ...WardrobeDayFragment
    ${withAppointments ? '...SchedulableAppointmentFragment' : ''}
    ${withAvailabilities ? '...WardrobeDayAvailabilitiesFragment' : ''}
  }
  month
  monthName
  weekDayNames
  year
  openingHours
  beginningOfWeek {
    ...WardrobeDayFragment
  }
  endOfWeek {
    ...WardrobeDayFragment
  }
  beginningOfDay {
    ...WardrobeDayFragment
  }
  endOfDay {
    ...WardrobeDayFragment
  }
  beginningOfMonth {
    ...WardrobeDayFragment
  }
  endOfMonth {
    ...WardrobeDayFragment
  }
}
${WardrobeDayFragment}
${withAppointments ? SchedulableAppointmentFragment : ''}
${withAvailabilities ? WardrobeDayAvailabilitiesFragment : ''}
  `;

  const WardrobeBookingCalendarFragment = gql`
fragment WardrobeBookingCalendarFragment on WardrobeCalendar {
  beginningOfDay {
    hours {
    ...SchedulableAvailabilityFragment
    }
  }
}
${SchedulableAvailabilityFragment}
  `;

  const CalendarToolFragment = gql`
fragment CalendarToolFragment on CalendarTool {
  next
  previous
  calendar {
    ...WardrobeCalendarFragment
  }
}
${WardrobeCalendarFragment}  
  `;

  const BookingCalendarToolFragment = gql`
fragment BookingCalendarToolFragment on CalendarTool {

  calendar {
    ...WardrobeBookingCalendarFragment
  }
}
${WardrobeBookingCalendarFragment}  
  `;

  const CalendarToolQuery = gql`
query calendarTool($date: String!, $view: String!${appointmentType ? ', $appointmentType: String' : ''}) {
  calendarTool(date: $date, view: $view) {
    ...CalendarToolFragment
  }
}  
${CalendarToolFragment}
  `;

  const BookingCalendarToolQuery = gql`
query calendarTool($date: String!, $view: String!${appointmentType ? ', $appointmentType: String' : ''}) {
  calendarTool(date: $date, view: $view) {
    ...BookingCalendarToolFragment
  }
}  
${BookingCalendarToolFragment}
  `;


  const useCalendarToolQuery = (date) => {
    const variables = { date, view: calendarType, appointmentType };

    return useQuery(CalendarToolQuery, {
      variables,
      fetchPolicy: 'network-only',
    });
  };

  const useBookingCalendarToolQuery = (date) => {
    const variables = { date, view: calendarType, appointmentType };

    return useQuery(BookingCalendarToolQuery, {
      variables,
      fetchPolicy: 'network-only',
    });
  };

  return {
    useCalendarToolQuery,
    useBookingCalendarToolQuery,
  };
};
